<script setup>
import {useRoute, useRouter} from "vue-router";
  import ClaimInfoComponent from "@/Components/ClaimInfo/ClaimInfoComponent.vue";
  import ClientCardComponent from "@/Components/ClientCard/ClientCardComponent.vue";
  import AgeInfoComponent from "@/Components/Claims/AgeInfoComponent.vue";
  import TimeExpenseComponent from "@/Components/TimeExpense/TimeExpenseComponent.vue";
  import Disbursements_MiscComponent from "@/Components/Disbursements_Misc/Disbursements_MiscComponent.vue";
  import InvoicesComponent from "@/Components/Invoices/InvoicesComponent.vue";
  import AcctManagerBtnComponent from "@/Components/AcctManagerBtn/AcctManagerBtnComponent.vue";
  import InsuredInfoComponent from "@/Components/Claims/InsuredInfoComponent.vue";
  import AdminsComponent from "@/Components/Admins/AdminsComponent.vue";
  import ListComponent from "@/Components/Tasks/ListComponent.vue";
  import AppointmentsMapComponent from "@/Components/Appointments/AppointmentsMapComponent.vue";
  import TimelineAdderComponent from "@/Components/TimelineAdder/TimelineAdderComponent.vue";
  import TimelineComponent from "@/Components/Timeline/TimelineComponent.vue";
  import AdjusterTimelineComponent from "@/Components/Timeline/AdjusterTimelineComponent.vue";
  import ClientTimelineComponent from "@/Components/Timeline/ClientTimelineComponent.vue";
  import FilesUploadComponent from "@/Components/Files/FilesUploadComponent.vue";
  import FilesListComponent from "@/Components/Files/FilesListComponent.vue";

  import {Permissions} from "@/Utils/Permissions";
  import {onMounted, reactive} from "vue";
  import {ApiWrapper} from "@/Utils/ApiWrapper";
  import {ToastUtil} from "@/Utils/ToastUtil";
  import {userStore} from "@/Stores/UserStore";

  const route = useRoute();
  const router = useRouter();

  const data = reactive({
    loading: true,
    exists: false,
    generalError: false
  });

  const checkExists = () => {
    data.loading = true;
    data.exists = false;
    ApiWrapper.post('claims', 'claim-exists', {
      claimId: route.params.claimId
    })
        .then((response) => {
          if(response){
            if(response.message === 'Claim exists'){
              data.exists = true;
            }
          }
        })
        .catch((e)=>{
          if(e.response && e.response.status === 404){
            data.exists = false;
          }
          else{
            data.generalError = true;
            ToastUtil.error(e);
          }
        })
        .finally(()=>{
          let user = userStore.userInfo
          //check if user is an AD Admin
          if(!user.adUser && !user.isAdmin){
            //check if user has Adjuster or Reviewer roles
            if(user.roles.includes('Adjuster') || user.roles.includes('Reviewer')){
              //check if user does NOT have the claims.unassigned permission (via another role)
              if(!Permissions.checkPermissions(['claims.unassigned'])){
                // check if they are assigned to the claim
                ApiWrapper.post('claim-info', 'get-user-role-count-on-claim', {
                  claimUuid: route.params.claimId,
                  userId: userStore.userInfo.id
                })
                .then((response) => {
                  if(response){
                    if(Object.hasOwn(response,'roleCount')){
                      if(response.roleCount < 1){
                        ToastUtil.warningMsg('You are not assigned to this claim');
                        router.push('/');
                      }
                    }
                  }
                })
                .catch((e)=>{
                  ToastUtil.error(e);
                  router.push('/');
                })
              }
            }
          }
          data.loading = false;
        });

  }
  onMounted(() => {
    checkExists();
  });
</script>

<template>
  <div v-if="!data.generalError">
    <template v-if="data.loading">
      <v-row>
        <v-col cols="12">
          <v-container fluid>
            <v-card class="elevation-2" height="auto" width="auto">
              <v-skeleton-loader type="card"></v-skeleton-loader>
            </v-card>
          </v-container>
        </v-col>
      </v-row>
    </template>

    <template v-if="!data.loading && !data.exists">
      <v-row>
        <v-col cols="12">
          <v-container fluid>
            <v-card class="elevation-2" height="auto" width="auto">
              <v-alert title="Not Found" text="The specified claim could not be found" type="error"></v-alert>
            </v-card>
          </v-container>
        </v-col>
      </v-row>
    </template>

    <template v-if="!data.loading && data.exists">
      <v-container fluid>
        <v-row>
          <v-col cols="6">
            <ClaimInfoComponent v-if="Permissions.checkPermissions(['claims.read'])" contextSchemaA="claims" :contextIdA="route.params.claimId"/>
            <v-row>
              <v-col cols="6" v-if="Permissions.checkPermissions(['claims.read-client'])">
                <ClientCardComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
              <v-col cols="6" v-if="Permissions.checkPermissions(['claims.age-info'])">
                <AgeInfoComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['invoices.list'])">
              <v-col cols="12">
                <TimeExpenseComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['disbursements.list'])">
              <v-col cols="12">
                <Disbursements_MiscComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['invoices.list'])">
              <v-col cols="12">
                <InvoicesComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['files.upload'])">
              <v-col cols="12">
                <FilesUploadComponent :claimId="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['files.list'])">
              <v-col cols="12">
                <FilesListComponent :claimId="route.params.claimId"/>
              </v-col>
            </v-row>


          </v-col>

          <v-col cols="6">
            <v-row v-if="Permissions.checkPermissions(['claims.account-manager-create-message'])">
              <v-col cols="12">
                <AcctManagerBtnComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" v-if="Permissions.checkPermissions(['claims.list-insured'])">
                <InsuredInfoComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
              <v-col cols="6" v-if="Permissions.checkPermissions(['claims.list-admins'])">
                <AdminsComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['tasks.list'])">
              <v-col cols="12">
                <ListComponent :claimId="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['appointments.list'])">
              <v-col cols="12">
                <AppointmentsMapComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['claims.timeline-adder'])">
              <v-col cols="12">
                <TimelineAdderComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-if="Permissions.checkPermissions(['timeline.admin-timeline'])">
              <v-col cols="12">
                <TimelineComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-else-if="Permissions.checkPermissions(['timeline.adjuster-timeline'])">
              <v-col cols="12">
                <AdjusterTimelineComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
            <v-row v-else-if="Permissions.checkPermissions(['timeline.client-timeline'])">
              <v-col cols="12">
                <ClientTimelineComponent contextSchemaA="claims" :contextIdA="route.params.claimId"/>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </div>
</template>

<style scoped>

</style>